import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, PanelBottom, PanelLeft } from 'lucide-react';
import type { Notification } from '@worki/shared';
import { currentLocale } from '../../i18n.js';
import { formatRelative } from '../../lib/format.js';
import { AnchoredMenu } from '../AnchoredMenu.js';
import { PageSearch } from '../PageSearch.js';
import { VoiceButton } from '../VoiceButton.js';
import { WorkiLogo } from '../WorkiLogo.js';
import { usePageSearchSlot } from '../../lib/pageSearch.js';
import { isQuoteEditorPath } from '../../lib/voice.js';
import { cn } from '../../lib/cn.js';
import { setNavigationMode, useNavigationMode } from '../../lib/navigationLayout.js';

export interface ShellNotification extends Notification {
  vars: Record<string, string | number>;
}

interface TopBarProps {
  title: string;
  items: ShellNotification[];
  unread: number;
  /** Open reminders are counted by the bell but are not notifications. */
  openReminders: number;
  onMarkAllRead: () => void;
  onOpenNotification: (notification: ShellNotification) => void;
  /*
   * ADM-004 — the platform's inbox is worked out from the accounts themselves
   * rather than written down as it happens, so there is nothing to mark read:
   * an approval is pending until someone approves it. The bell says so by not
   * offering the button, instead of offering one that would do nothing.
   */
  readable?: boolean;
  /** The central inbox, or the separate platform staff inbox. */
  viewAllTo?: string;
  activityTo?: string;
}

export function TopBar({
  title,
  items,
  unread,
  openReminders,
  onMarkAllRead,
  onOpenNotification,
  readable = true,
  viewAllTo = '/notifications',
  activityTo,
}: TopBarProps) {
  const { t } = useTranslation();
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slot = usePageSearchSlot();
  const navigation = useNavigationMode();

  const badge = unread + openReminders;

  return (
    /* The bar carries the brand, screen name, desktop navigation toggle and bell.
       The hamburger that used to sit at the end of it opened a
       second, longer copy of the rail — so a phone had two menus, one of them
       hidden behind an icon that says nothing about what is inside. The screens
       it reached that the bottom bar has no slot for are listed on the personal
       area instead, which is itself one of those slots. */
    <header className="sticky top-0 z-40 bg-card/90 backdrop-blur-md border-b border-border shadow-sm pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        <div className="group/bar flex items-center gap-3 min-w-0">
          <Link
            to="/"
            aria-label={t('shell.home')}
            className="text-primary hover:opacity-80 transition-opacity shrink-0"
          >
            <WorkiLogo className="h-6 w-auto" aria-hidden />
          </Link>
          {/* On a phone an open search box takes the title's place. Beside a
              long title it was squeezed until nothing could be typed into it
              (PageSearch says how that broke the invoices search); the screen
              is still named by the bottom bar. */}
          <span className="h-5 w-px bg-border shrink-0 max-md:group-has-[input]/bar:hidden" />
          <h1 className="text-base font-semibold text-foreground truncate max-md:group-has-[input]/bar:hidden">{title}</h1>
          {/* UI-11 — beside the name of the screen, on whichever screen has a
              search. The term belongs to the screen; only the box is here, so
              there is one place to look for it instead of a magnifier on three
              screens and a toolbar box on five others. */}
          {slot && (
            <PageSearch
              value={slot.value}
              onChange={slot.onChange}
              {...(slot.placeholder ? { placeholder: slot.placeholder } : {})}
              busy={Boolean(slot.busy)}
              className="ms-1"
            />
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            data-navigation-toggle
            aria-label={t(navigation === 'sidebar' ? 'shell.navigationToBottom' : 'shell.navigationToSidebar')}
            title={t(navigation === 'sidebar' ? 'shell.navigationToBottom' : 'shell.navigationToSidebar')}
            onClick={() => setNavigationMode(navigation === 'sidebar' ? 'bottom' : 'sidebar')}
            className="hidden md:grid min-h-11 min-w-11 place-items-center rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          >
            {navigation === 'sidebar' ? <PanelBottom className="h-5 w-5" aria-hidden /> : <PanelLeft className="h-5 w-5" aria-hidden />}
          </button>
          {/* A spoken note starts a quote from any screen: the editor reads the
              job off the address and fills a new quote with it. The button
              renders nothing when the business has no voice feature, and is
              left out over the quote editor, which has its own. */}
          {!isQuoteEditorPath(pathname) && (
            <VoiceButton agent onReady={(job) => navigate(`/quotes/new?voiceJob=${job.id}`)} />
          )}
          {/* Notifications — half-screen popup */}
          <div className="relative">
            <button
              type="button"
              ref={notifRef}
              onClick={() => setShowNotifs(!showNotifs)}
              aria-label={t('nav.notifications')}
              aria-expanded={showNotifs}
              className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-muted transition-colors relative"
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {badge > 0 && <span className="absolute top-1.5 end-1.5 h-2 w-2 rounded-full bg-destructive" />}
            </button>
            {/* Portalled: the panel is taller than the bar it hangs from, and
                a header that is `sticky` is a stacking context of its own. */}
            <AnchoredMenu
              open={showNotifs}
              onClose={() => setShowNotifs(false)}
              anchorRef={notifRef}
              align="end"
              width={320}
            >
              <NotificationsPanel
                items={items}
                unread={unread}
                openReminders={openReminders}
                readable={readable}
                onMarkAllRead={onMarkAllRead}
                onOpenReminders={() => {
                  setShowNotifs(false);
                  navigate('/reminders');
                }}
                onOpenNotification={(n) => {
                  onOpenNotification(n);
                  setShowNotifs(false);
                  if (n.link) navigate(n.link);
                }}
                onViewAll={() => {
                  setShowNotifs(false);
                  navigate(viewAllTo);
                }}
                {...(activityTo ? { onOpenActivity: () => { setShowNotifs(false); navigate(activityTo); } } : {})}
              />
            </AnchoredMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

interface NotificationsPanelProps {
  items: ShellNotification[];
  unread: number;
  openReminders: number;
  readable: boolean;
  onMarkAllRead: () => void;
  onOpenReminders: () => void;
  onOpenNotification: (notification: ShellNotification) => void;
  onViewAll: () => void;
  onOpenActivity?: () => void;
}

/**
 * What the bell opens. Exported so its markup can be rendered on its own.
 *
 * 12.4 — only the list scrolls. "View all" used to sit at the end of it, inside
 * the part that scrolls, so with a long list the way to everything was the last
 * thing reached. The title with "mark all read" stays at the top of the panel
 * and "view all" at the bottom, whatever the length of the list between them.
 */
export function NotificationsPanel({
  items,
  unread,
  openReminders,
  readable,
  onMarkAllRead,
  onOpenReminders,
  onOpenNotification,
  onViewAll,
  onOpenActivity,
}: NotificationsPanelProps) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <div className="max-h-[50vh] flex flex-col">
      <div className="shrink-0 p-3 border-b border-border flex items-center justify-between">
        <span className="text-sm font-semibold">{t('notifications.title')}</span>
        {readable && (
          <button
            type="button"
            onClick={onMarkAllRead}
            disabled={unread === 0}
            className="min-h-11 px-2 text-xs text-primary hover:underline disabled:opacity-40 disabled:hover:no-underline"
          >
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
        {openReminders > 0 && (
          <button
            type="button"
            onClick={onOpenReminders}
            className="w-full px-3 py-2.5 flex items-center gap-2.5 text-start bg-warning/10 hover:bg-warning/15 transition-colors"
          >
            <div className="h-2 w-2 rounded-full shrink-0 bg-warning" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {t('notifications.openReminders', { count: openReminders })}
              </p>
              <p className="text-[10px] text-muted-foreground">{t('notifications.openRemindersHint')}</p>
            </div>
          </button>
        )}
        {items.length === 0 && openReminders === 0 && (
          <p className="px-3 py-6 text-sm text-muted-foreground text-center">{t('notifications.empty')}</p>
        )}
        {items.map((n) => {
          const unreadRow = !n.readAt;
          return (
            <button
              type="button"
              key={n.id}
              className={cn(
                'w-full px-3 py-2.5 flex items-center gap-2.5 text-start cursor-pointer hover:bg-muted/50 transition-colors',
                unreadRow && 'bg-primary/5',
              )}
              onClick={() => onOpenNotification(n)}
            >
              <div className={cn('h-2 w-2 rounded-full shrink-0', unreadRow ? 'bg-primary' : 'bg-transparent')} />
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm truncate', unreadRow && 'font-medium')}>{t(n.title, n.vars)}</p>
                <p className="text-[10px] text-muted-foreground">{formatRelative(n.createdAt, locale)}</p>
              </div>
            </button>
          );
        })}
      </div>
      {onOpenActivity && <button type="button" onClick={onOpenActivity} className="shrink-0 min-h-11 w-full px-3 py-2.5 text-start text-sm text-primary hover:bg-muted/50 border-t border-border font-medium">
        {t('nav.activity')}
      </button>}
      <button
        type="button"
        onClick={onViewAll}
        className="shrink-0 min-h-11 w-full py-2.5 text-center text-sm text-primary hover:bg-muted/50 border-t border-border font-medium"
      >
        {t('shell.viewAllNotifications')}
      </button>
    </div>
  );
}
